import { useEffect, useState, type JSX } from 'react';
import { Box } from '@chakra-ui/react';
import { IFollowUpArea, ServiceProviderType } from 'api-paths-cas';
import { DocAccessType, IFollowUpDocuments } from '../../../../../types';
import NoDocuments from '../../../../atoms/no-doc-placeholder';
import {
  hasPerm,
  isAgentOrAgencyRole,
  isEnrolyStaff,
  isPartnerRole,
  isUniStaffRole,
  P,
} from '../../../../../utilities/roles/permissions';
import { UserRole } from '../../../../../store/auth/types';
import { FileUploadModal } from '../../../modals/fileUpload/index';
import { IModal } from '../../../../../store/ui/types';
import CustomRequestDocumentModal from '../../../modals/customRequestDoc/container';
import FileDeleteModal from '../../../modals/fileDelete/container';
import FileRejectModal from '../../../modals/fileReject/container';
import StampedVisaVignette from '../../../../molecules/followUpDocumentSection/afterArrival/stampedVisaVignette';
import BiometricResidencePermit from '../../../../molecules/followUpDocumentSection/afterArrival/biometricResidencePermit';
import CustomRequestDocument from '../../../../molecules/followUpDocumentSection/customRequest';
import FileRescindRejectionModal from '../../../modals/fileRescindRejection';
import { useDispatch, useSelector } from 'react-redux';
import { getStudentFollowUpDocs } from '../../../../../store/student/actions';
import { DocumentRequestButtons } from '../../../../DocumentRequestButtons';
import { SectionGroup } from '../../../../Finger/SectionGroup';
import { DocumentViewer } from '../../../../organisms/DocumentViewer';
import { ApplicationState } from '@/store';

export interface IDispatchProps {
  openFollowUpModal: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle?: string,
    data?: Record<string, unknown>,
  ) => void;

  onApproveDoc: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    docStatus?: string,
    docPrevStatus?: string,
  ) => void;

  openUploadModal: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string,
    modalTitle: string,
    fileNames: string[],
    metadata?: unknown,
  ) => void;
  closeModal: () => void;
  saveShareCode: (docKey: string, data: Record<string, unknown>) => void;
}

export interface IProps {
  hasRequiredAfterArrivalDocs: boolean;
  hasStaffOnlyAfterArrivalDocs: boolean;
  hasAgentOnlyAfterArrivalDocs: boolean;
  openModal: IModal | null;
  documents: IFollowUpDocuments;
  dob?: string;
  userRole: UserRole;
  serviceProviderType?: ServiceProviderType;
  isAfterArrivalAreaHidden: boolean;
  afterArrivalDocsRequired: number;
}

type Props = IProps & IDispatchProps;

function AfterTravelDocumentsDashboard(props: Props): JSX.Element {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getStudentFollowUpDocs());
  }, []);

  const currentUserRole = useSelector(({ auth }: ApplicationState) => auth.role);

  const isAgent = isAgentOrAgencyRole(currentUserRole);
  const isStaff = isUniStaffRole(currentUserRole);
  const isEnrolyOrPartner = isEnrolyStaff(currentUserRole) || isPartnerRole(currentUserRole);

  const canResume = hasPerm(props.userRole, P.CHECKLIST_EDIT);
  const canUpload = hasPerm(props.userRole, P.FOLLOW_UP_DOCUMENTS_UPLOAD);
  const canOpen = hasPerm(props.userRole, P.ALL_DOCUMENTS_PREVIEW, props.serviceProviderType);
  const canApprove = hasPerm(props.userRole, P.ALL_DOCUMENTS_APPROVE);
  const canReject = hasPerm(props.userRole, P.ALL_DOCUMENTS_REJECT);

  const canUploadStaffOnly = hasPerm(props.userRole, P.UPLOAD_STAFF_ONLY_DOCS) && (isStaff || isEnrolyOrPartner);
  const canUploadAgentOnly =
    hasPerm(props.userRole, P.UPLOAD_AGENT_ONLY_DOCS) && (isAgent || isStaff || isEnrolyOrPartner);

  const canRequest = hasPerm(props.userRole, P.CUSTOM_DOCUMENTS_REQUEST);
  const canDelete = hasPerm(props.userRole, P.FOLLOW_UP_DOCUMENTS_DELETE);

  const hasStaffOnlyDocs = props.hasStaffOnlyAfterArrivalDocs && canUploadStaffOnly;
  const hasAgentOnlyDocs = props.hasAgentOnlyAfterArrivalDocs && canUploadAgentOnly;

  const [isDeleting, setIsDeleting] = useState(false);

  const [previewOpen, toggleOpen] = useState(false);
  const [imageArray, setImageArray] = useState<string[] | undefined>();

  const { openModal } = props;
  if (openModal?.isUpload) {
    return <FileUploadModal {...openModal} />;
  }
  if (openModal) {
    if (openModal.sponsorType === 'customRequestModal') {
      return <CustomRequestDocumentModal followUpArea={IFollowUpArea.AFTER_ARRIVAL} />;
    }
    if (openModal?.docType?.startsWith('delete-')) {
      return <FileDeleteModal onDelete={() => setIsDeleting(false)} />;
    }
    if (openModal?.docType?.startsWith('reject-')) {
      return <FileRejectModal />;
    }
    if (openModal.docType?.startsWith('rescind-reject-')) {
      return <FileRescindRejectionModal />;
    }
  }

  const renderAfterArrivalDocs = (userAccess?: DocAccessType): JSX.Element => {
    return (
      <>
        {!props.isAfterArrivalAreaHidden && (
          <>
            <StampedVisaVignette
              userAccess={userAccess}
              documents={props.documents.afterArrival.stampedVisaVignette}
              canResume={canResume}
              canUpload={canUpload}
              canOpen={canOpen}
              canApprove={canApprove}
              canReject={canReject}
              isDeleting={isDeleting}
              onAcknowledgeClick={() => {}}
              onApproveClick={(docKey: string) => {
                props.onApproveDoc(
                  'afterArrival',
                  null,
                  'stampedVisaVignette',
                  docKey,
                  props.documents.afterArrival.stampedVisaVignette[docKey].status,
                  props.documents.afterArrival.stampedVisaVignette[docKey].prevStatus,
                );
              }}
              onDeleteClick={(docKey: string) => {
                props.openFollowUpModal(
                  'afterArrival',
                  null,
                  'delete-stampedVisaVignette',
                  docKey,
                  'Stamped Visa / eVisa',
                );
              }}
              onOpenClick={(docKey: string) => {
                setImageArray(props.documents.afterArrival.stampedVisaVignette[docKey].signedUrls);
                toggleOpen(true);
              }}
              onRejectClick={(docKey: string) => {
                props.openFollowUpModal(
                  'afterArrival',
                  null,
                  'reject-stampedVisaVignette',
                  docKey,
                  'Stamped Visa / eVisa',
                );
              }}
              onRescindRejectClick={(docKey: string) => {
                props.openFollowUpModal(
                  'afterArrival',
                  null,
                  'rescind-reject-stampedVisaVignette',
                  docKey,
                  'Stamped Visa / eVisa',
                );
              }}
              onUploadClick={(docKey: string) => {
                props.openUploadModal(
                  'afterArrival',
                  null,
                  'stampedVisaVignette',
                  docKey,
                  'Stamped Visa / eVisa',
                  props.documents.afterArrival.stampedVisaVignette[docKey].fileNames,
                );
              }}
              userRole={props.userRole}
            />

            <BiometricResidencePermit
              userAccess={userAccess}
              documents={props.documents.afterArrival.biometricResidencePermit}
              canResume={canResume}
              canUpload={canUpload}
              canOpen={canOpen}
              canApprove={canApprove}
              canReject={canReject}
              isDeleting={isDeleting}
              onAcknowledgeClick={() => {}}
              onApproveClick={(docKey: string) => {
                props.onApproveDoc(
                  'afterArrival',
                  null,
                  'biometricResidencePermit',
                  docKey,
                  props.documents.afterArrival.biometricResidencePermit[docKey].status,
                  props.documents.afterArrival.biometricResidencePermit[docKey].prevStatus,
                );
              }}
              onDeleteClick={(docKey: string) => {
                props.openFollowUpModal(
                  'afterArrival',
                  null,
                  'delete-biometricResidencePermit',
                  docKey,
                  'Stamped Visa / eVisa',
                );
              }}
              onOpenClick={(docKey: string) => {
                setImageArray(props.documents.afterArrival.biometricResidencePermit[docKey].signedUrls);
                toggleOpen(true);
              }}
              onRejectClick={(docKey: string) => {
                props.openFollowUpModal(
                  'afterArrival',
                  null,
                  'reject-biometricResidencePermit',
                  docKey,
                  'Biometric Residence Permit',
                );
              }}
              onRescindRejectClick={(docKey: string) => {
                props.openFollowUpModal(
                  'afterArrival',
                  null,
                  'rescind-reject-biometricResidencePermit',
                  docKey,
                  'Biometric Residence Permit',
                );
              }}
              onUploadClick={(docKey: string) => {
                props.openUploadModal(
                  'afterArrival',
                  null,
                  'biometricResidencePermit',
                  docKey,
                  'Biometric Residence Permit',
                  props.documents.afterArrival.biometricResidencePermit[docKey].fileNames,
                );
              }}
              userRole={props.userRole}
            />
          </>
        )}

        <CustomRequestDocument
          userAccess={userAccess}
          documents={props.documents.afterArrival.customRequest}
          canUpload={canUpload}
          canOpen={canOpen}
          canApprove={canApprove}
          canReject={canReject}
          isDeleting={isDeleting}
          onOpenClick={(customReqKey: string, docKey: string) => {
            setImageArray(props.documents.afterArrival.customRequest[customReqKey][docKey].signedUrls ?? []);
            toggleOpen(true);
          }}
          onUploadClick={(customReqKey: string, docKey: string) =>
            props.openUploadModal(
              'afterArrival',
              customReqKey,
              'customRequest',
              docKey,
              'Custom Request',
              props.documents.afterArrival.customRequest[customReqKey][docKey].fileNames,
            )
          }
          onApproveClick={(customReqKey: string, docKey: string) =>
            props.onApproveDoc(
              'afterArrival',
              customReqKey,
              'customRequest',
              docKey,
              props.documents.afterArrival.customRequest[customReqKey][docKey].status,
              props.documents.afterArrival.customRequest[customReqKey][docKey].prevStatus,
            )
          }
          onRejectClick={(customReqKey: string, docKey: string) => {
            props.openFollowUpModal('afterArrival', customReqKey, 'reject-customRequest', docKey, 'Custom Document');
          }}
          onRescindRejectClick={(customReqKey: string, docKey: string) => {
            props.openFollowUpModal(
              'afterArrival',
              customReqKey,
              'rescind-reject-customRequest',
              docKey,
              'Custom Document',
            );
          }}
          onDeleteClick={(customReqKey: string, docKey: string) => {
            props.openFollowUpModal('afterArrival', customReqKey, 'delete-customRequest', docKey, 'Custom Document');
          }}
          userRole={props.userRole}
        />
      </>
    );
  };

  const hasCustomAfterArrivalDocs = Object.keys(props.documents.afterArrival.customRequest ?? {}).length > 0;

  return (
    <Box>
      {props.hasRequiredAfterArrivalDocs || hasStaffOnlyDocs || hasAgentOnlyDocs || hasCustomAfterArrivalDocs ? (
        <>
          {(props.hasRequiredAfterArrivalDocs || hasCustomAfterArrivalDocs) && (
            <SectionGroup
              heading="After Arrival in the UK"
              description="These documents are requested from the student after arrival"
              data-selector={'after-arrival-area'}
            >
              {renderAfterArrivalDocs()}
            </SectionGroup>
          )}

          {hasStaffOnlyDocs && (
            <SectionGroup
              heading="The below documents can only be uploaded by a staff member"
              description="The student has not been asked to provide these documents"
              data-selector={'staff-only-docs-section'}
            >
              {props.hasStaffOnlyAfterArrivalDocs ? renderAfterArrivalDocs(DocAccessType.STAFF_ONLY) : null}
            </SectionGroup>
          )}

          {hasAgentOnlyDocs && (
            <SectionGroup
              heading="The below documents can only be uploaded by an agent"
              description="The student has not been asked to provide these documents"
              data-selector={'agent-only-docs-section'}
            >
              {props.hasAgentOnlyAfterArrivalDocs ? renderAfterArrivalDocs(DocAccessType.AGENT_ONLY) : null}
            </SectionGroup>
          )}
        </>
      ) : (
        <NoDocuments />
      )}

      <DocumentRequestButtons
        canRequest={canRequest}
        canDelete={canDelete}
        isDeleting={isDeleting}
        onRequestClick={() =>
          props.openFollowUpModal('customRequestModal', null, null, null, 'Custom Document Request')
        }
        onDeleteClick={() => setIsDeleting(!isDeleting)}
      />

      {previewOpen && imageArray && imageArray.length > 0 && (
        <DocumentViewer documents={imageArray} onClose={() => toggleOpen(false)} />
      )}
    </Box>
  );
}

export default AfterTravelDocumentsDashboard;
